import { v4 as uuid } from 'uuid';

import {
  DataField,
  DatePickerDataField,
  ReviewStageDataFieldTypeEnum,
  TextDataField,
} from 'components/FormBuilder/schemas';

import {
  GROUPED_QUESTION_OPTIONS,
  HIRING_MANAGER_PAY_RATE_KEY,
  HIRING_MANAGER_START_DATE_KEY,
  StandardQuestion,
} from './constants';

export type CreateDefaultCommonFieldArgs = {
  position: number;
  question?: string | null;
  hint?: string | null;
  key?: string | null;
};
export const createDefaultCommonField = ({
  position,
  question = '',
  hint = '',
  key = '',
}: CreateDefaultCommonFieldArgs): Omit<DataField, 'type'> & {
  _id: string;
} => ({
  _id: uuid(),
  question: question ?? '',
  hint: hint ?? '',
  key: key ?? '',
  position,
  required: true,
});

export const createDefaultTextField = (
  args: CreateDefaultCommonFieldArgs,
): TextDataField => ({
  type: ReviewStageDataFieldTypeEnum.text_field,
  ...createDefaultCommonField(args),
});

export const createDefaultDatePickerField = (
  args: CreateDefaultCommonFieldArgs,
): DatePickerDataField => ({
  type: ReviewStageDataFieldTypeEnum.datepicker,
  ...createDefaultCommonField(args),
});

export const createDefaultPayRateField = (
  args: CreateDefaultCommonFieldArgs,
): TextDataField => ({
  type: ReviewStageDataFieldTypeEnum.text_field,
  ...createDefaultCommonField(args),
  key: HIRING_MANAGER_PAY_RATE_KEY,
  hiring_manager_standard_question: true,
});

export const createDefaultStartDateField = (
  args: CreateDefaultCommonFieldArgs,
): DatePickerDataField => ({
  ...createDefaultDatePickerField(args),
  key: HIRING_MANAGER_START_DATE_KEY,
  hiring_manager_standard_question: true,
});

export const STANDARD_QUESTION_DEFAULTS: Record<
  StandardQuestion['value'],
  typeof createDefaultPayRateField | typeof createDefaultStartDateField
> = {
  [HIRING_MANAGER_PAY_RATE_KEY]: createDefaultPayRateField,
  [HIRING_MANAGER_START_DATE_KEY]: createDefaultStartDateField,
};

// A questionnaire asks each standard question at most once, so the picker
// hides the ones it already asks, and the whole group once none are left.
export const getGroupedQuestionOptions = ({
  usedKeys,
}: {
  usedKeys: Array<string>;
}) =>
  GROUPED_QUESTION_OPTIONS.map((group) =>
    group.label === 'custom'
      ? {
          ...group,
          options: group.options.filter(
            ({ value }) => !usedKeys.includes(value),
          ),
        }
      : group,
  ).filter(({ options }) => options.length > 0);
